import moment from "moment-timezone";
import { PERIOD_FILTERS, API_CONSTANTS, DATE_FORMATS } from "./constants";

const YEARS_BEFORE = 5;
const YEARS_AFTER = 1;

export const CALENDAR_YEARS = Array.from(
  { length: YEARS_BEFORE + YEARS_AFTER + 1 },
  (_, i) => new Date().getFullYear() - YEARS_BEFORE + i,
);

/**
 * Get date range based on period filter
 * @param {string} periodFilter - The period filter (YTD, Monthly, Weekly, Daily, Quarterly)
 * @param {Date} referenceDate - Reference date (defaults to current date)
 * @returns {object} Object with startDate and endDate
 */
export const getDateRangeByPeriod = (periodFilter, referenceDate = new Date()) => {
  const now = new Date(referenceDate);
  const currentYear = now.getFullYear();

  switch (periodFilter) {
    case PERIOD_FILTERS.PYTD:
      return {
        startDate: new Date(currentYear - 1, 0, 1),
        endDate: now,
      };
    case PERIOD_FILTERS.YTD:
      return {
        startDate: new Date(currentYear, 0, 1), // January 1st of current year
        endDate: now,
      };

    case PERIOD_FILTERS.MONTHLY:
      return {
        startDate: new Date(now.getFullYear(), now.getMonth(), 1), // First day of current month
        endDate: now,
      };

    case PERIOD_FILTERS.WEEKLY:
      const dayOfWeek = now.getDay();
      const diff = now.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // Monday of current week
      const weekStart = new Date(now);
      weekStart.setDate(diff);
      return {
        startDate: weekStart,
        endDate: new Date(),
      };

    case PERIOD_FILTERS.QUARTERLY:
      const currentMonth = now.getMonth();
      const quarter = Math.floor(currentMonth / 3); // 0-3 (Q1, Q2, Q3, Q4)
      const quarterStartMonth = quarter * 3; // 0, 3, 6, or 9
      const quarterEndMonth = (quarter + 1) * 3 - 1; // 2, 5, 8, or 11
      return {
        startDate: new Date(currentYear, quarterStartMonth, 1), // First day of quarter
        endDate: new Date(currentYear, quarterEndMonth + 1, 0), // Last day of quarter
      };

    case PERIOD_FILTERS.DAILY:
    default:
      return {
        startDate: new Date(now.getFullYear(), now.getMonth(), now.getDate()), // Start of today
        endDate: now,
      };
  }
};

/**
 * Get month date range for a specific month
 * @param {Date} monthDate - Date representing the month
 * @returns {object} Object with startDate and endDate for the whole month
 */
export const getMonthDateRange = (monthDate) => {
  const startDate = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const endDate = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
  return { startDate, endDate };
};

/**
 * Versatile date/time utility for America/New_York timezone
 * @param {Object} options - Configuration options
 * @param {Date|string} options.date - Input date (defaults to current time)
 * @param {string} options.format - Output format:
 *   - 'date-object' (default): Returns Date object in timezone
 *   - 'date-string': Returns 'YYYY-MM-DD'
 *   - 'datetime-string': Returns 'YYYY-MM-DD HH:mm:ss'
 * @param {boolean} options.useCurrentTime - If true, uses current time in timezone
 *   instead of input date's time (useful for service dates)
 * @returns {Date|string} Formatted date based on options
 */
export const getDateInTimezone = (options = {}) => {
  const { date = new Date(), format = "date-object", useCurrentTime = false } = options;

  const inputDate = new Date(date);
  const now = new Date();

  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: API_CONSTANTS.DEFAULT_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

  const nowParts = formatter.formatToParts(now);
  const getNowPart = (type) => nowParts.find((p) => p.type === type).value;

  let year, month, day, hours, minutes, seconds;

  if (useCurrentTime) {
    // Use date from input, time from current timezone
    year = inputDate.getFullYear();
    month = String(inputDate.getMonth() + 1).padStart(2, "0");
    day = String(inputDate.getDate()).padStart(2, "0");
    hours = getNowPart("hour");
    minutes = getNowPart("minute");
    seconds = getNowPart("second");
  } else {
    // Use everything from current timezone
    year = getNowPart("year");
    month = getNowPart("month");
    day = getNowPart("day");
    hours = getNowPart("hour");
    minutes = getNowPart("minute");
    seconds = getNowPart("second");
  }

  switch (format) {
    case "date-string":
      return `${year}-${month}-${day}`;
    case "datetime-string":
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    case "date-object":
    default:
      return new Date(
        parseInt(year),
        parseInt(month) - 1,
        parseInt(day),
        parseInt(hours),
        parseInt(minutes),
        parseInt(seconds)
      );
  }
};

/**
 * Format date for date keys (YYYY-MM-DD)
 * @param {Date} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Get user's device/browser timezone
 * @returns {string} IANA timezone string (e.g., "America/Los_Angeles", "Europe/London")
 */
export const getUserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (e) {
    return API_CONSTANTS.DEFAULT_TIMEZONE;
  }
};

/**
 * Get current date/time in America/New_York timezone
 * Returns a Date object representing "now" in EST/EDT
 * @returns {Date} Date object in America/New_York timezone
 */
export const getNowInTimezone = () => getDateInTimezone();

/**
 * Round number to specified decimal places
 * @param {number} value - Value to round
 * @param {number} decimalPlaces - Number of decimal places (default: 2)
 * @returns {number} Rounded value
 */
export const roundToPrecision = (value, decimalPlaces = 2) => {
  const multiplier = Math.pow(10, decimalPlaces);
  return Math.round(value * multiplier) / multiplier;
};

/**
 * Format hours and minutes display
 * @param {number} hours - Hours
 * @param {number} minutes - Minutes
 * @returns {string} Formatted time string (e.g., "18 H 30 M")
 */
export const formatHoursMinutes = (hours, minutes) => {
  const totalHours = hours + Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${totalHours} H ${remainingMinutes} M`;
};

/**
 * Format currency amount
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount) => {
  return `$ ${Number(amount).toFixed(2).toLocaleString()}`;
};

/**
 * Generate date range string for display
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @returns {string} Formatted date range string
 */
export const formatDateRange = (startDate, endDate) => {
  const startMonth = startDate.toLocaleString("default", { month: "short" });
  const endMonth = endDate.toLocaleString("default", { month: "short" });
  const startYear = startDate.getFullYear();
  const endYear = endDate.getFullYear();

  return `${startMonth} ${startDate.getDate()}, ${startYear} - ${endMonth} ${endDate.getDate()}, ${endYear}`;
};

/**
 * Get start of month in server timezone (America/New_York)
 * @param {Date} date - Date representing a month
 * @returns {string} Formatted date string 'YYYY-MM-DD HH:mm:ss'
 */
export const getStartOfMonthInServerTz = (date) => {
  const d = moment(date);
  return moment
    .tz([d.year(), d.month(), d.date()], API_CONSTANTS.DEFAULT_TIMEZONE)
    .startOf("month")
    .format("YYYY-MM-DD HH:mm:ss");
};

/**
 * Get end of month in server timezone (America/New_York)
 * @param {Date} date - Date representing a month
 * @returns {string} Formatted date string 'YYYY-MM-DD HH:mm:ss'
 */
export const getEndOfMonthInServerTz = (date) => {
  const d = moment(date);
  return moment
    .tz([d.year(), d.month(), d.date()], API_CONSTANTS.DEFAULT_TIMEZONE)
    .endOf("month")
    .format("YYYY-MM-DD HH:mm:ss");
};

export const formatCardDate = (isoDate) => {
  return moment(isoDate).format("MMMM D, YYYY");
};
